import gsap from 'gsap';
import { $isVideoAutoplayable } from '../stores/state';

export default class MediasSlider extends HTMLElement {
    static CALL_EVENT = 'togglePlay';
    static DEFAULT_DURATION = 5;
    static LOAD_NEXT_DIFFERENCE_THRESHOLD = 3;

    constructor() {
        super();

        /* Binding */
        this.onScrollCall = this.onScrollCall.bind(this);
        this.onVideoDurationChange = this.onVideoDurationChange.bind(this);
        this.onVideoTimeUpdate = this.onVideoTimeUpdate.bind(this);
        this.onVideoEnded = this.onVideoEnded.bind(this);
        this.onCtaMousenter = this.onCtaMousenter.bind(this);
        this.onCtaClick = this.onCtaClick.bind(this);
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);

        /* UI */
        this.$items = Array.from(this.querySelectorAll('[data-item]'));
        this.$ctas = Array.from(this.querySelectorAll('[data-cta]'));
        this.$progresses = Array.from(this.querySelectorAll('[data-progress]'));
        this.$activeItem = null;
        this.$prevCta = this.querySelector('[data-prev]');
        this.$nextCta = this.querySelector('[data-next]');

        /* Data */
        this.uid = this.dataset.uid;
        this.subtime = 0;
        this.thresholdTime = -1;
        this.loadingNextThresholdTime = -1;
        this.activeIndex = -1;
        this.isComponentReady = false;
        this.hasAlreadyPlay = false;
        this.hasAlreadyGoTo = false;
        this.isInview = false;
        this.isLoadingRequested = false;
        this.loadedItems = [];
    }

    // =============================================================================
    // Lifecycle methods
    // =============================================================================
    connectedCallback() {
        this.goTo(0);
        this.bindEvents();
    }

    disconnectedCallback() {
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        window.addEventListener(`${MediasSlider.CALL_EVENT}${this.uid}`, this.onScrollCall);

        this.$ctas.forEach(($cta) => {
            $cta.addEventListener('mouseenter', this.onCtaMousenter, { once: true });
            $cta.addEventListener('click', this.onCtaClick);
        });

        this.$prevCta.addEventListener('click', this.onPrevClick);
        this.$nextCta.addEventListener('click', this.onNextClick);
    }

    unbindEvents() {
        window.removeEventListener(`${MediasSlider.CALL_EVENT}${this.uid}`, this.onScrollCall);

        this.loadedItems.forEach((item) => {
            if (item.$video) {
                item.$video.removeEventListener('durationchange', this.onVideoDurationChange);
                item.$video.removeEventListener('timeupdate', this.onVideoTimeUpdate);
                item.$video.removeEventListener('ended', this.onVideoEnded);
            } else if (item.tl) {
                item.tl.kill();
            }
        });

        this.$ctas.forEach(($cta) => {
            $cta.removeEventListener('mouseenter', this.onCtaMousenter, { once: true });
            $cta.removeEventListener('click', this.onCtaClick);
        });

        this.$prevCta.removeEventListener('click', this.onPrevClick);
        this.$nextCta.removeEventListener('click', this.onNextClick);
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onScrollCall(event) {
        // Handle scroll enter/leave events for autoplay
        const { way } = event.detail;

        switch (way) {
            case 'enter':
                this.isInview = true;
                this.play();
                break;
            case 'leave':
                this.isInview = false;
                this.pause();
                break;
        }
    }

    onCtaMousenter(event) {
        const index = this.$ctas.indexOf(event.target);
        this.setItem(index);
    }

    onCtaClick(event) {
        const index = this.$ctas.indexOf(event.target);

        if (index === this.activeIndex) return;

        this.isLoadingRequested = false;

        this.$activeItem?.$video?.pause?.();
        this.$activeItem?.tl?.pause?.();

        requestAnimationFrame(() => {
            this.setProgress(0);
            this.goTo(index);
        });
    }

    onPrevClick() {
        this.isLoadingRequested = false;
        this.pause();
        this.setProgress(0);
        this.goTo(this.activeIndex - 1 < 0 ? this.$items.length - 1 : this.activeIndex - 1);
    }

    onNextClick() {
        this.isLoadingRequested = false;
        this.pause();
        this.setProgress(0);
        this.goTo((this.activeIndex + 1) % this.$items.length);
    }

    onVideoDurationChange(event) {
        this.isComponentReady = true;

        const $item = this.loadedItems.find((item) => item.$video === event.target);
        $item.duration = event.target.duration;
        $item.ready = true;
        $item.resolver();
    }

    onVideoTimeUpdate(event) {
        if (this.thresholdTime > -1) {
            this.setProgress(event.target.currentTime / this.thresholdTime);
        }

        if (
            this.loadingNextThresholdTime > -1 &&
            event.target.currentTime >= this.loadingNextThresholdTime &&
            !this.isLoadingRequested
        ) {
            this.isLoadingRequested = true;
            this.setItem((this.activeIndex + 1) % this.$items.length);
        }
    }

    onVideoEnded(event) {
        this.isLoadingRequested = false;
        this.pause();
        this.setProgress(0);
        this.goTo((this.activeIndex + 1) % this.$items.length);
    }

    // =============================================================================
    // Methods
    // =============================================================================
    setItem(index) {
        const $item = this.$items[index];

        switch ($item.nodeName) {
            case 'VIDEO':
                return this.setVideo($item);
            default:
                return this.setImage($item);
        }
    }

    setVideo($video) {
        return new Promise((resolve) => {
            if (this.loadedItems.find((item) => item.$video === $video && item.ready))
                return resolve();

            this.loadedItems.push({
                type: 'video',
                $video,
                duration: null,
                ready: false,
                resolver: resolve
            });

            $video.addEventListener('durationchange', this.onVideoDurationChange);
            $video.addEventListener('timeupdate', this.onVideoTimeUpdate);
            $video.addEventListener('ended', this.onVideoEnded);
            $video.setAttribute('preload', 'metadata');
        });
    }

    setImage($image) {
        return new Promise((resolve) => {
            if (this.loadedItems.find((item) => item.$image === $image && item.ready))
                return resolve();

            $image.style.display = 'block';
            this.loadedItems.push({
                type: 'image',
                $image,
                ready: true,
                tl: gsap.timeline({
                    duration: MediasSlider.DEFAULT_DURATION,
                    paused: true,
                    onComplete: () => {
                        this.isLoadingRequested = false;
                        this.setProgress(0);
                        this.goTo((this.activeIndex + 1) % this.$items.length);
                    },
                    onUpdate: () => {
                        const progress = this.loadedItems
                            .find((item) => item.$image === $image)
                            .tl.progress();

                        this.setProgress(progress);

                        if (
                            this.loadingNextThresholdTime > -1 &&
                            progress * this.thresholdTime >= this.loadingNextThresholdTime &&
                            !this.isLoadingRequested
                        ) {
                            this.isLoadingRequested = true;
                            this.setItem((this.activeIndex + 1) % this.$items.length);
                        }
                    }
                })
            });

            this.isComponentReady = true;

            resolve();
        });
    }

    async play() {
        if (!this.isComponentReady) return;

        if (!this.hasAlreadyGoTo) {
            this.goTo((this.activeIndex + 1) % this.$items.length);
        }

        if (this.$activeItem?.type === 'video') {
            try {
                if ($isVideoAutoplayable.value) {
                    await this.$activeItem.$video.play();
                    $isVideoAutoplayable.set(true);
                }
            } catch (err) {
                $isVideoAutoplayable.set(false);
            }
        } else if (this.$activeItem?.type === 'image') {
            this.$activeItem.tl.play();
        }
    }

    pause() {
        this.$activeItem?.$video?.pause?.();
        this.$activeItem?.tl?.pause?.();
    }

    async goTo(index) {
        await this.setItem(index);

        const $domItem = this.$items[index];
        const $targetItem = this.loadedItems.find(
            (item) => item.$video === $domItem || item.$image === $domItem
        );
        if (!$targetItem) return;

        this.hasAlreadyGoTo = true;

        this.setActive(index);

        switch ($targetItem.type) {
            case 'video':
                this.goToVideo($targetItem);
                break;
            case 'image':
                this.goToImage($targetItem);
                break;
        }
    }

    goToVideo(videoObj) {
        const $video = videoObj.$video;
        this.thresholdTime = $video.duration;
        this.loadingNextThresholdTime =
            $video.duration - MediasSlider.LOAD_NEXT_DIFFERENCE_THRESHOLD;
        this.$activeItem = videoObj;
        $video.currentTime = 0;

        if (this.isInview) {
            this.play();
        }
    }

    goToImage(imageObj) {
        this.thresholdTime = MediasSlider.DEFAULT_DURATION;
        this.loadingNextThresholdTime =
            MediasSlider.DEFAULT_DURATION - MediasSlider.LOAD_NEXT_DIFFERENCE_THRESHOLD;
        this.$activeItem = imageObj;
        imageObj.tl.progress(0);

        if (this.isInview) {
            this.play();
        }
    }

    setActive(index) {
        this.$items[this.oldIndex]?.parentElement?.classList.remove('is-prev');
        this.$items[this.activeIndex]?.parentElement?.classList.add('is-prev');
        this.$items[this.activeIndex]?.parentElement?.classList.remove('is-active');

        this.oldIndex = this.activeIndex;
        this.activeIndex = index;

        requestAnimationFrame(() => {
            this.$items[this.activeIndex].parentElement.classList.add('is-active');
        });
    }

    setProgress(progress) {
        this.$progresses[this.activeIndex] &&
            (this.$progresses[this.activeIndex].style.transform = `scaleX(${progress})`);
    }
}
